export const mapChoiceByOptions = (val, options) => {
  if (val === undefined || val === null) return "";
  const raw = `${val}`.trim();
  const lowerRaw = raw.toLowerCase();
  const byValue = options.find((opt) => opt.value.toLowerCase() === lowerRaw);
  if (byValue) return byValue.value;
  const byLabel = options.find((opt) => opt.label.trim().toLowerCase() === lowerRaw);
  return byLabel ? byLabel.value : lowerRaw;
};

export const normalizeChoiceForPayload = (val, options) => {
  const normalized = mapChoiceByOptions(val, options);
  return normalized === "" ? null : normalized;
};

export const formatChoiceLabel = (val, options) => {
  const normalized = mapChoiceByOptions(val, options);
  const match = options.find((opt) => opt.value === normalized);
  return match ? match.label : val || "";
};

export const commodityListFromValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((c) => (c || "").toString().trim()).filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
  }
  return [];
};

export const normalizeCommodityInputList = (list) => {
  const normalized = [];
  const seen = new Set();
  (list || []).forEach((item) => {
    const clean = (item || "").toString().trim();
    if (!clean) return;
    const lower = clean.toLowerCase();
    if (seen.has(lower)) return;
    seen.add(lower);
    normalized.push(clean);
  });
  return normalized;
};

const normalizeCommodityList = (value) =>
  commodityListFromValue(value)
    .map((c) => c.toLowerCase())
    .filter(Boolean);

export const areCommodityListsEqual = (a, b) => {
  const A = normalizeCommodityList(a);
  const B = normalizeCommodityList(b);
  if (A.length !== B.length) return false;
  const setB = new Set(B);
  for (const item of A) {
    if (!setB.has(item)) return false;
  }
  return true;
};

export const normalizeCommoditiesForPayload = (value) =>
  normalizeCommodityInputList(commodityListFromValue(value));

export const rowsFromBackend = (value) => {
  if (!value) return [{ exchange: "", ticker: "" }];
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (typeof value === "string") {
    const raw = value.trim();
    try {
      const parsed = JSON.parse(raw.replace(/'/g, '"'));
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
  } else if (typeof value === "object") {
    list = Object.entries(value).map(([exchange, ticker]) => ({
      exchange,
      ticker,
    }));
  }
  if (!list.length) return [{ exchange: "", ticker: "" }];
  return list.map((r) => ({
    exchange: (r?.exchange || "").toString().trim().toUpperCase(),
    ticker: (r?.ticker || "").toString().trim().toUpperCase(),
  }));
};

export const normalizeRows = (rows) =>
  (rows || [])
    .map((r) => ({
      exchange: (r.exchange || "").trim().toUpperCase(),
      ticker: (r.ticker || "").trim().toUpperCase(),
    }))
    .filter((r) => r.exchange && r.ticker);

export const hasDuplicatePairs = (rows) => {
  const seen = new Set();
  for (const r of normalizeRows(rows)) {
    const key = `${r.exchange}::${r.ticker}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

export const hasIncompleteRows = (rows) =>
  (rows || []).some((r) => {
    const ex = (r.exchange || "").trim();
    const tk = (r.ticker || "").trim();
    return (ex && !tk) || (!ex && tk);
  });

const normalizeBackendArray = (value) => normalizeRows(rowsFromBackend(value));

export const areTickerArraysEqual = (a, b) => {
  const A = normalizeBackendArray(a);
  const B = normalizeBackendArray(b);
  const setA = new Set(A.map((r) => `${r.exchange}::${r.ticker}`));
  const setB = new Set(B.map((r) => `${r.exchange}::${r.ticker}`));
  if (setA.size !== setB.size) return false;
  for (const k of setA) if (!setB.has(k)) return false;
  return true;
};

export const normalizeFieldValue = (field, value) => {
  if (value === undefined) return undefined;
  if (field === "year_founded") {
    if (value === "" || value === null) return null;
    const intVal = parseInt(value, 10);
    return Number.isNaN(intVal) ? null : intVal;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? "" : trimmed;
  }
  return value;
};

export const ensureUrl = (value) => {
  if (!value) return "";
  return value.includes("://") ? value : `https://${value}`;
};

export const getEmbedUrl = (value) => {
  if (!value) return "";
  const normalized = ensureUrl(value);
  try {
    const url = new URL(normalized);
    if (url.hostname.includes("youtube.com")) {
      const videoId = url.searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.hostname === "youtu.be") {
      const videoId = url.pathname.replace("/", "").trim();
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }
    return normalized;
  } catch {
    return normalized;
  }
};
